import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import CenterFocusStrongOutlinedIcon from '@mui/icons-material/CenterFocusStrongOutlined';
import ViewListOutlinedIcon from '@mui/icons-material/ViewListOutlined';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import type { KubernetesConsoleApi } from './api';
import { useKubernetesWatch } from './useKubernetesWatch';
import type { K8sAlert, K8sObject } from './types';

const COLORS = {
  green: '#92c55e',
  amber: '#f4c145',
  red: '#f0561d',
  blue: '#8bc1f7',
  text: '#f0f0f0',
  muted: '#c7c7c7',
  border: '#4f5255',
  surface: '#1f1f1f',
  card: '#272727',
};

interface TopologyNode {
  id: string;
  kind: string;
  section: string;
  object: K8sObject;
  x: number;
  y: number;
  health: string;
  alerts: number;
  donut?: { ready: number; pending: number; failed: number; total: number };
}

interface TopologyEdge {
  from: string;
  to: string;
}

const COLUMNS = [
  { kind: 'Deployment', section: 'deployments', x: 100, header: 'WORKLOADS' },
  { kind: 'StatefulSet', section: 'statefulsets', x: 100 },
  { kind: 'DaemonSet', section: 'daemonsets', x: 100 },
  { kind: 'ReplicaSet', section: 'replicasets', x: 320, header: 'REPLICASET' },
  { kind: 'Pod', section: 'pods', x: 540, header: 'POD' },
  { kind: 'Service', section: 'services', x: 760, header: 'SERVICE' },
  { kind: 'Route', section: 'routes', x: 980, header: 'ROUTE' },
];

const DONUT_KINDS = new Set(['Deployment', 'StatefulSet', 'DaemonSet']);
const MIN_SCALE = 0.4;
const MAX_SCALE = 2.5;
const ALERTS_POLL_MS = 30_000;

function selectorMatches(
  selector: Record<string, string> | undefined,
  labels: Record<string, string> | undefined,
): boolean {
  if (!selector || !Object.keys(selector).length) return false;
  return Object.entries(selector).every(
    ([key, value]) => labels?.[key] === value,
  );
}

function classifyPodStatus(object: K8sObject): 'ready' | 'pending' | 'failed' {
  const waiting = (object.status?.containerStatuses ?? [])
    .map((status: any) => status.state?.waiting?.reason)
    .find(Boolean);
  if (
    object.status?.phase === 'Failed' ||
    waiting === 'CrashLoopBackOff' ||
    waiting === 'ImagePullBackOff'
  ) {
    return 'failed';
  }
  if (
    object.status?.phase !== 'Running' ||
    (object.status?.containerStatuses ?? []).some(
      (status: any) => !status.ready,
    )
  ) {
    return 'pending';
  }
  return 'ready';
}

function nodeHealth(kind: string, object: K8sObject): string {
  if (kind === 'Pod') {
    const status = classifyPodStatus(object);
    return status === 'failed'
      ? COLORS.red
      : status === 'pending'
      ? COLORS.amber
      : COLORS.green;
  }
  if (
    kind === 'Deployment' ||
    kind === 'StatefulSet' ||
    kind === 'ReplicaSet'
  ) {
    const desired = object.spec?.replicas ?? 0;
    const ready = object.status?.readyReplicas ?? 0;
    return ready >= desired
      ? COLORS.green
      : ready === 0
      ? COLORS.red
      : COLORS.amber;
  }
  if (kind === 'DaemonSet') {
    const desired = object.status?.desiredNumberScheduled ?? 0;
    const ready = object.status?.numberReady ?? 0;
    return ready >= desired
      ? COLORS.green
      : ready === 0
      ? COLORS.red
      : COLORS.amber;
  }
  return COLORS.blue;
}

function podsForWorkload(
  kind: string,
  name: string,
  pods: K8sObject[],
  replicaSets: K8sObject[],
): K8sObject[] {
  if (kind === 'Deployment') {
    const ownedReplicaSetNames = new Set(
      replicaSets
        .filter(
          rs =>
            rs.metadata.ownerReferences?.some(
              owner => owner.kind === 'Deployment' && owner.name === name,
            ),
        )
        .map(rs => rs.metadata.name),
    );
    return pods.filter(
      pod =>
        pod.metadata.ownerReferences?.some(
          owner =>
            owner.kind === 'ReplicaSet' && ownedReplicaSetNames.has(owner.name),
        ),
    );
  }
  return pods.filter(
    pod =>
      pod.metadata.ownerReferences?.some(
        owner => owner.kind === kind && owner.name === name,
      ),
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function DonutRing({
  cx,
  cy,
  donut,
}: {
  cx: number;
  cy: number;
  donut: { ready: number; pending: number; failed: number; total: number };
}) {
  const r = 13;
  const strokeWidth = 4;
  const circumference = 2 * Math.PI * r;
  if (donut.total === 0) {
    return (
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill={COLORS.card}
        stroke="#3a3a3a"
        strokeWidth={strokeWidth}
      />
    );
  }
  const segments = [
    { count: donut.ready, color: COLORS.green },
    { count: donut.pending, color: COLORS.amber },
    { count: donut.failed, color: COLORS.red },
  ].filter(segment => segment.count > 0);
  let cumulative = 0;
  return (
    <g>
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill={COLORS.card}
        stroke="#3a3a3a"
        strokeWidth={strokeWidth}
      />
      {segments.map((segment, index) => {
        const fraction = segment.count / donut.total;
        const dashLength = fraction * circumference;
        const offset = -cumulative;
        cumulative += dashLength;
        return (
          <circle
            key={`${segment.color}-${index}`}
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={segment.color}
            strokeWidth={strokeWidth}
            strokeDasharray={`${dashLength} ${circumference - dashLength}`}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${cx} ${cy})`}
            strokeLinecap="butt"
          />
        );
      })}
    </g>
  );
}

function statusLabel(kind: string, object: K8sObject): string {
  if (kind === 'Pod') {
    return object.status?.phase ?? 'Unknown';
  }
  if (
    kind === 'Deployment' ||
    kind === 'StatefulSet' ||
    kind === 'ReplicaSet'
  ) {
    const desired = object.spec?.replicas ?? 0;
    const ready = object.status?.readyReplicas ?? 0;
    return `${ready}/${desired} ready`;
  }
  if (kind === 'DaemonSet') {
    const desired = object.status?.desiredNumberScheduled ?? 0;
    const ready = object.status?.numberReady ?? 0;
    return `${ready}/${desired} ready`;
  }
  if (kind === 'Service') {
    return object.spec?.type ?? 'ClusterIP';
  }
  if (kind === 'Route') {
    return object.spec?.host ?? 'route';
  }
  return '';
}

export function TopologyPanel({
  api,
  cluster,
  namespace,
  onOpen,
  highlighted,
}: {
  api: KubernetesConsoleApi;
  cluster: string;
  namespace: string;
  onOpen: (section: string, object: K8sObject) => void;
  highlighted?: { kind: string; name: string };
}) {
  const [alerts, setAlerts] = useState<K8sAlert[]>([]);
  const [alertsError, setAlertsError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'graph' | 'list'>('graph');
  const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1 });
  const [isPanning, setIsPanning] = useState(false);
  const panStateRef = useRef<{
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const canWatch = Boolean(cluster && namespace);
  const nsBase = `/namespaces/${encodeURIComponent(namespace)}`;
  const deploymentWatch = useKubernetesWatch(
    api,
    cluster,
    canWatch ? `/apis/apps/v1${nsBase}/deployments` : null,
  );
  const statefulSetWatch = useKubernetesWatch(
    api,
    cluster,
    canWatch ? `/apis/apps/v1${nsBase}/statefulsets` : null,
  );
  const daemonSetWatch = useKubernetesWatch(
    api,
    cluster,
    canWatch ? `/apis/apps/v1${nsBase}/daemonsets` : null,
  );
  const replicaSetWatch = useKubernetesWatch(
    api,
    cluster,
    canWatch ? `/apis/apps/v1${nsBase}/replicasets` : null,
  );
  const podWatch = useKubernetesWatch(
    api,
    cluster,
    canWatch ? `/api/v1${nsBase}/pods` : null,
  );
  const serviceWatch = useKubernetesWatch(
    api,
    cluster,
    canWatch ? `/api/v1${nsBase}/services` : null,
  );
  const routeWatch = useKubernetesWatch(
    api,
    cluster,
    canWatch ? `/apis/route.openshift.io/v1${nsBase}/routes` : null,
  );

  const watches = useMemo(
    () => [
      deploymentWatch,
      statefulSetWatch,
      daemonSetWatch,
      replicaSetWatch,
      podWatch,
      serviceWatch,
      routeWatch,
    ],
    [
      deploymentWatch,
      statefulSetWatch,
      daemonSetWatch,
      replicaSetWatch,
      podWatch,
      serviceWatch,
      routeWatch,
    ],
  );

  const resources: Record<string, K8sObject[]> = useMemo(
    () => ({
      Deployment: deploymentWatch.items,
      StatefulSet: statefulSetWatch.items,
      DaemonSet: daemonSetWatch.items,
      ReplicaSet: replicaSetWatch.items,
      Pod: podWatch.items,
      Service: serviceWatch.items,
      Route: routeWatch.items,
    }),
    [
      deploymentWatch.items,
      statefulSetWatch.items,
      daemonSetWatch.items,
      replicaSetWatch.items,
      podWatch.items,
      serviceWatch.items,
      routeWatch.items,
    ],
  );

  const allConnected = canWatch && watches.every(watch => watch.connected);
  const anyReconnecting = watches.some(watch => watch.reconnecting);
  const initialLoading =
    canWatch && watches.some(watch => !watch.connected && !watch.error);
  const totalFailure =
    canWatch &&
    !initialLoading &&
    watches.every(
      watch => watch.error && !watch.connected && watch.items.length === 0,
    );
  const firstError = watches.find(watch => watch.error)?.error ?? null;

  useEffect(() => {
    let cancelled = false;
    async function loadAlerts() {
      try {
        const result = await api.namespaceAlerts(cluster, namespace);
        if (!cancelled) {
          setAlerts(result.alerts);
          setAlertsError(null);
        }
      } catch (loadError) {
        if (!cancelled) {
          setAlertsError(
            loadError instanceof Error ? loadError.message : String(loadError),
          );
        }
      }
    }
    loadAlerts();
    const interval = window.setInterval(loadAlerts, ALERTS_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [api, cluster, namespace]);

  useEffect(() => {
    setTransform({ x: 0, y: 0, scale: 1 });
  }, [cluster, namespace]);

  const graph = useMemo(() => {
    const nodes: TopologyNode[] = [];
    const nodeById = new Map<string, TopologyNode>();
    const rowByColumn = new Map<number, number>();
    for (const column of COLUMNS) {
      (resources[column.kind] ?? []).forEach(object => {
        const row = rowByColumn.get(column.x) ?? 0;
        rowByColumn.set(column.x, row + 1);
        const id = `${column.kind}/${object.metadata.name}`;
        const alertLabel =
          column.kind === 'Pod'
            ? 'pod'
            : column.kind === 'Deployment'
            ? 'deployment'
            : column.kind === 'Service'
            ? 'service'
            : undefined;
        const alertCount = alertLabel
          ? alerts.filter(
              alert => alert.labels[alertLabel] === object.metadata.name,
            ).length
          : 0;
        const node: TopologyNode = {
          id,
          kind: column.kind,
          section: column.section,
          object,
          x: column.x,
          y: 75 + row * 92,
          health: nodeHealth(column.kind, object),
          alerts: alertCount,
        };
        if (DONUT_KINDS.has(column.kind)) {
          const ownedPods = podsForWorkload(
            column.kind,
            object.metadata.name,
            resources.Pod ?? [],
            resources.ReplicaSet ?? [],
          );
          const counts = { ready: 0, pending: 0, failed: 0 };
          for (const pod of ownedPods) {
            const status = classifyPodStatus(pod);
            counts[status] += 1;
          }
          node.donut = { ...counts, total: ownedPods.length };
        }
        nodes.push(node);
        nodeById.set(id, node);
      });
    }

    const edges: TopologyEdge[] = [];
    for (const node of nodes) {
      const owner = node.object.metadata.ownerReferences?.[0];
      if (owner && nodeById.has(`${owner.kind}/${owner.name}`)) {
        edges.push({ from: `${owner.kind}/${owner.name}`, to: node.id });
      }
    }
    const pods = resources.Pod ?? [];
    for (const service of resources.Service ?? []) {
      for (const pod of pods) {
        if (selectorMatches(service.spec?.selector, pod.metadata.labels)) {
          edges.push({
            from: `Pod/${pod.metadata.name}`,
            to: `Service/${service.metadata.name}`,
          });
        }
      }
    }
    for (const route of resources.Route ?? []) {
      const serviceName = route.spec?.to?.name;
      if (serviceName && nodeById.has(`Service/${serviceName}`)) {
        edges.push({
          from: `Service/${serviceName}`,
          to: `Route/${route.metadata.name}`,
        });
      }
    }
    return { nodes, edges, nodeById };
  }, [alerts, resources]);

  function zoomBy(factor: number, anchor?: { x: number; y: number }) {
    setTransform(current => {
      const nextScale = clamp(current.scale * factor, MIN_SCALE, MAX_SCALE);
      const ratio = nextScale / current.scale;
      const rect = containerRef.current?.getBoundingClientRect();
      const anchorX = anchor?.x ?? (rect ? rect.width / 2 : 0);
      const anchorY = anchor?.y ?? (rect ? rect.height / 2 : 0);
      return {
        scale: nextScale,
        x: anchorX - (anchorX - current.x) * ratio,
        y: anchorY - (anchorY - current.y) * ratio,
      };
    });
  }

  function handleWheel(event: React.WheelEvent<HTMLDivElement>) {
    event.preventDefault();
    const rect = containerRef.current?.getBoundingClientRect();
    const anchor = rect
      ? { x: event.clientX - rect.left, y: event.clientY - rect.top }
      : undefined;
    zoomBy(event.deltaY < 0 ? 1.1 : 1 / 1.1, anchor);
  }

  function handleSvgPointerDown(event: React.PointerEvent<SVGSVGElement>) {
    if (event.target !== event.currentTarget) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    panStateRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: transform.x,
      originY: transform.y,
    };
    setIsPanning(true);
  }

  function handleSvgPointerMove(event: React.PointerEvent<SVGSVGElement>) {
    const panState = panStateRef.current;
    if (!panState) return;
    const dx = event.clientX - panState.startX;
    const dy = event.clientY - panState.startY;
    setTransform(current => ({
      ...current,
      x: panState.originX + dx,
      y: panState.originY + dy,
    }));
  }

  function handleSvgPointerUp(event: React.PointerEvent<SVGSVGElement>) {
    panStateRef.current = null;
    setIsPanning(false);
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // pointer may already be released
    }
  }

  function resetView() {
    setTransform({ x: 0, y: 0, scale: 1 });
  }

  if (initialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }
  if (totalFailure) return <Alert severity="error">{firstError}</Alert>;

  const contentHeight = Math.max(
    420,
    ...[...new Set(COLUMNS.map(column => column.x))].map(
      x =>
        120 +
        COLUMNS.filter(column => column.x === x).reduce(
          (count, column) => count + (resources[column.kind]?.length ?? 0),
          0,
        ) *
          92,
    ),
  );
  const viewportHeight = Math.min(640, Math.max(420, contentHeight));

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: 1, flexWrap: 'wrap', rowGap: 1 }}
      >
        <Tooltip
          title={
            alertsError ??
            firstError ??
            (allConnected
              ? 'Live Kubernetes watch connected'
              : 'Using request-based refresh')
          }
        >
          <Chip
            size="small"
            variant="outlined"
            label={
              allConnected
                ? 'Live'
                : anyReconnecting
                ? 'Reconnecting'
                : 'Refresh'
            }
            sx={{
              height: 20,
              fontSize: 10,
              color: allConnected
                ? COLORS.green
                : anyReconnecting
                ? COLORS.amber
                : COLORS.muted,
              borderColor: allConnected
                ? `${COLORS.green}88`
                : anyReconnecting
                ? `${COLORS.amber}88`
                : COLORS.border,
            }}
          />
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        {viewMode === 'graph' && (
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: COLORS.green,
              }}
            />
            <Typography sx={{ fontSize: 10, color: COLORS.muted }}>
              Ready
            </Typography>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: COLORS.amber,
                ml: 0.75,
              }}
            />
            <Typography sx={{ fontSize: 10, color: COLORS.muted }}>
              Pending
            </Typography>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: COLORS.red,
                ml: 0.75,
              }}
            />
            <Typography sx={{ fontSize: 10, color: COLORS.muted }}>
              Failed
            </Typography>
          </Stack>
        )}
        {viewMode === 'graph' && (
          <Stack direction="row" spacing={0.25}>
            <Tooltip title="Zoom out">
              <IconButton size="small" onClick={() => zoomBy(1 / 1.2)}>
                <ZoomOutIcon fontSize="small" sx={{ color: COLORS.muted }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Zoom in">
              <IconButton size="small" onClick={() => zoomBy(1.2)}>
                <ZoomInIcon fontSize="small" sx={{ color: COLORS.muted }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reset view">
              <IconButton size="small" onClick={resetView}>
                <CenterFocusStrongOutlinedIcon
                  fontSize="small"
                  sx={{ color: COLORS.muted }}
                />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
        <Stack direction="row" spacing={0.25}>
          <Tooltip title="Graph view">
            <IconButton
              size="small"
              onClick={() => setViewMode('graph')}
              sx={{
                bgcolor:
                  viewMode === 'graph' ? `${COLORS.blue}22` : 'transparent',
              }}
            >
              <AccountTreeOutlinedIcon
                fontSize="small"
                sx={{
                  color: viewMode === 'graph' ? COLORS.blue : COLORS.muted,
                }}
              />
            </IconButton>
          </Tooltip>
          <Tooltip title="List view">
            <IconButton
              size="small"
              onClick={() => setViewMode('list')}
              sx={{
                bgcolor:
                  viewMode === 'list' ? `${COLORS.blue}22` : 'transparent',
              }}
            >
              <ViewListOutlinedIcon
                fontSize="small"
                sx={{ color: viewMode === 'list' ? COLORS.blue : COLORS.muted }}
              />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {viewMode === 'list' ? (
        <Box
          sx={{
            border: '1px solid #4f5255',
            borderRadius: 2,
            bgcolor: '#1f1f1f',
            p: 1.5,
          }}
        >
          {COLUMNS.filter(
            (column, index) =>
              COLUMNS.findIndex(other => other.kind === column.kind) === index,
          ).map(column => {
            const nodes = graph.nodes.filter(node => node.kind === column.kind);
            return (
              <Box key={column.kind} sx={{ mb: 2 }}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 0.5 }}
                >
                  <Typography
                    sx={{ fontSize: 12, fontWeight: 700, color: COLORS.muted }}
                  >
                    {column.kind.toUpperCase()}
                  </Typography>
                  <Chip
                    size="small"
                    label={nodes.length}
                    sx={{
                      height: 18,
                      fontSize: 10,
                      bgcolor: `${COLORS.blue}12`,
                      color: COLORS.blue,
                    }}
                  />
                </Stack>
                {nodes.length === 0 ? (
                  <Typography sx={{ fontSize: 12, color: COLORS.muted, pl: 1 }}>
                    None
                  </Typography>
                ) : (
                  <Stack spacing={0.5}>
                    {nodes.map(node => (
                      <Box
                        key={node.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => onOpen(node.section, node.object)}
                        onKeyDown={event => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            onOpen(node.section, node.object);
                          }
                        }}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          px: 1,
                          py: 0.75,
                          borderRadius: 1,
                          cursor: 'pointer',
                          bgcolor:
                            highlighted?.kind === node.kind &&
                            highlighted.name === node.object.metadata.name
                              ? '#25364f'
                              : 'transparent',
                          '&:hover': { bgcolor: '#2a2a2a' },
                        }}
                      >
                        <Box
                          sx={{
                            width: 9,
                            height: 9,
                            borderRadius: '50%',
                            bgcolor: node.health,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          sx={{ fontSize: 12, color: COLORS.text, flex: 1 }}
                        >
                          {node.object.metadata.name}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: COLORS.muted }}>
                          {statusLabel(node.kind, node.object)}
                        </Typography>
                        {node.alerts > 0 && (
                          <Chip
                            size="small"
                            label={node.alerts}
                            sx={{
                              height: 18,
                              fontSize: 10,
                              bgcolor: `${COLORS.red}22`,
                              color: COLORS.red,
                            }}
                          />
                        )}
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            );
          })}
          {!graph.nodes.length && (
            <Typography sx={{ color: COLORS.muted, p: 2 }}>
              No workload topology was found in this namespace.
            </Typography>
          )}
        </Box>
      ) : (
        <Box
          ref={containerRef}
          onWheel={handleWheel}
          sx={{
            overflow: 'hidden',
            border: '1px solid #4f5255',
            borderRadius: 2,
            bgcolor: '#1f1f1f',
            height: viewportHeight,
            position: 'relative',
          }}
        >
          <svg
            width="100%"
            height="100%"
            role="group"
            aria-label="Kubernetes workload topology"
            onPointerDown={handleSvgPointerDown}
            onPointerMove={handleSvgPointerMove}
            onPointerUp={handleSvgPointerUp}
            style={{
              touchAction: 'none',
              cursor: isPanning ? 'grabbing' : 'grab',
            }}
          >
            <g
              transform={`translate(${transform.x} ${transform.y}) scale(${transform.scale})`}
            >
              {COLUMNS.filter(column => column.header).map(column => (
                <text
                  key={column.kind}
                  x={column.x}
                  y={28}
                  textAnchor="middle"
                  fill="#c7c7c7"
                  fontSize="12"
                  fontWeight="700"
                >
                  {column.header}
                </text>
              ))}
              {graph.edges.map((edge, index) => {
                const from = graph.nodeById.get(edge.from);
                const to = graph.nodeById.get(edge.to);
                if (!from || !to) return null;
                return (
                  <path
                    key={`${edge.from}-${edge.to}-${index}`}
                    d={`M ${from.x + 76} ${from.y} C ${from.x + 125} ${
                      from.y
                    }, ${to.x - 125} ${to.y}, ${to.x - 76} ${to.y}`}
                    fill="none"
                    stroke="#6a6e73"
                    strokeWidth="1.5"
                  />
                );
              })}
              {graph.nodes.map(node => {
                const isHighlighted =
                  highlighted?.kind === node.kind &&
                  highlighted.name === node.object.metadata.name;
                const hasDonut = Boolean(node.donut);
                return (
                  <g
                    key={node.id}
                    data-node="true"
                    role="button"
                    tabIndex={0}
                    aria-label={`Open ${node.kind} ${node.object.metadata.name}`}
                    onClick={() => onOpen(node.section, node.object)}
                    onKeyDown={event => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        onOpen(node.section, node.object);
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <rect
                      x={node.x - 76}
                      y={node.y - 30}
                      width="152"
                      height="60"
                      rx="12"
                      fill={isHighlighted ? '#25364f' : COLORS.card}
                      stroke={
                        isHighlighted
                          ? COLORS.blue
                          : hasDonut
                          ? '#4f5255'
                          : node.health
                      }
                      strokeWidth={isHighlighted ? 5 : 3}
                    />
                    <text
                      x={hasDonut ? node.x + 8 : node.x}
                      y={node.y - 5}
                      textAnchor="middle"
                      fill="#f0f0f0"
                      fontSize="12"
                      fontWeight="700"
                    >
                      {node.object.metadata.name.length > 18
                        ? `${node.object.metadata.name.slice(0, 16)}…`
                        : node.object.metadata.name}
                    </text>
                    <text
                      x={hasDonut ? node.x + 8 : node.x}
                      y={node.y + 14}
                      textAnchor="middle"
                      fill="#c7c7c7"
                      fontSize="10"
                    >
                      {node.kind}
                    </text>
                    {node.donut && (
                      <DonutRing
                        cx={node.x - 60}
                        cy={node.y}
                        donut={node.donut}
                      />
                    )}
                    {node.alerts > 0 && (
                      <>
                        <circle
                          cx={node.x + 68}
                          cy={node.y - 26}
                          r="12"
                          fill="#f0561d"
                        />
                        <text
                          x={node.x + 68}
                          y={node.y - 22}
                          textAnchor="middle"
                          fill="#fff"
                          fontSize="10"
                          fontWeight="700"
                        >
                          {node.alerts}
                        </text>
                      </>
                    )}
                  </g>
                );
              })}
            </g>
          </svg>
          {!graph.nodes.length && (
            <Typography
              sx={{
                color: '#c7c7c7',
                p: 2,
                position: 'absolute',
                top: 0,
                left: 0,
              }}
            >
              No workload topology was found in this namespace.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
